import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const Examination = () => {
  const screeningTests = [
    {
      icon: 'ri-stethoscope-line',
      title: '心不全を疑わせる症状',
      items: [
        '息切れ、呼吸困難',
        '動悸、不整脈',
        '下肢のむくみ',
        '体重増加',
        '疲労感、倦怠感',
      ],
    },
    {
      icon: 'ri-test-tube-line',
      title: 'BNP/NT-proBNP検査',
      items: [
        '血液検査で測定可能',
        '心臓への負担を数値化',
        'BNP > 100 pg/mL で心不全を疑う',
        'NT-proBNP > 400 pg/mL で心不全を疑う',
        'スクリーニングに有用',
      ],
    },
  ];

  const detailedTests = [
    {
      icon: 'ri-run-line',
      title: '心肺運動負荷試験（CPX）',
      desc: '運動中の心肺機能を評価し、運動耐容能の低下を客観的に判定します。最大酸素摂取量（peak VO2）や嫌気性代謝閾値（AT）などを測定し、心不全の重症度評価や運動処方に活用します。',
      color: 'from-blue-50 to-blue-100',
    },
    {
      icon: 'ri-heart-pulse-line',
      title: '心エコー検査',
      desc: '超音波を用いて心臓の構造と機能を評価します。経胸壁心エコー検査が基本で、より詳細な評価が必要な場合は経食道心エコー検査や運動負荷心エコー検査を実施します。左室駆出率（LVEF）や弁膜症の評価が可能です。',
      color: 'from-red-50 to-red-100',
    },
    {
      icon: 'ri-scan-line',
      title: 'CT・MRI検査',
      desc: '心臓の詳細な画像を撮影し、心筋の状態や冠動脈の評価を行います。CTは冠動脈の狭窄評価に、MRIは心筋の線維化や浮腫の評価に優れています。造影剤を使用することで、より詳細な情報が得られます。',
      color: 'from-green-50 to-green-100',
    },
    {
      icon: 'ri-radioactive-line',
      title: '心筋シンチグラム',
      desc: '放射性同位元素を用いて心筋の血流や代謝を評価します。虚血性心疾患の診断や心筋のバイアビリティ（生存能）の評価に有用です。負荷時と安静時の画像を比較することで、虚血の範囲を特定できます。',
      color: 'from-purple-50 to-purple-100',
    },
    {
      icon: 'ri-syringe-line',
      title: '心臓カテーテル検査',
      desc: '入院して行う精密検査です。カテーテルを心臓まで挿入し、冠動脈造影や心内圧測定を行います。冠動脈疾患の確定診断や、必要に応じて経皮的冠動脈形成術（PCI）などの治療も同時に実施可能です。',
      color: 'from-orange-50 to-orange-100',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-24">
        <section className="py-20 bg-gradient-to-br from-primary-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-6">
              検査方法
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl">
              心不全の診断に必要な各種検査について詳しくご説明します
            </p>
          </div>
        </section>

        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">検査の流れ</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  心不全の診断は、まずスクリーニング検査から始まり、必要に応じて精密検査へと進みます。患者さんの状態に応じて、最適な検査を選択し、正確な診断と適切な治療方針の決定を目指します。
                </p>
                <p className="text-gray-700 leading-relaxed">
                  各検査は専門のスタッフが丁寧に実施し、結果について分かりやすくご説明いたします。検査に関するご不安やご質問がございましたら、お気軽にお声がけください。
                </p>
              </div>
              <div className="w-full h-96 rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/67fad561c15b02f8995ddcb6e5e1726d.jpg"
                  alt="検査の流れ"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">スクリーニング検査</h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-base">
                まずは心不全を疑わせる症状と血液検査（BNP/NT-proBNP）でスクリーニングを行います
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
              {screeningTests.map((test, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-primary-50 border-2 border-primary-200 p-10 rounded-3xl hover:shadow-2xl transition-all"
                >
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 flex items-center justify-center bg-primary-500 rounded-2xl flex-shrink-0">
                      <i className={`${test.icon} text-3xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4">{test.title}</h3>
                      <ul className="space-y-2">
                        {test.items.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-700 text-sm">
                            <i className="ri-check-line text-primary-500 mt-1 flex-shrink-0"></i>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-medical-beige p-10 rounded-3xl mb-20">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 flex items-center justify-center bg-yellow-500 rounded-xl flex-shrink-0">
                  <i className="ri-information-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">スクリーニング後の流れ</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    心不全症状やBNP/NT-proBNPの値から心不全を疑う場合は、当院をはじめとする急性期病院での専門医の診察をお勧めしております。港区では「みなと心不全協議会」が中心となり、住民健診での心不全リスク評価（心不全健診）や、循環器専門施設へのスムーズな紹介体制を整え、早期発見・介入を推進しています。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">精密検査</h2>
              <p className="text-gray-600 max-w-3xl mx-auto text-base">
                運動耐容能の評価や原因疾患の精査のため、以下の検査を実施します
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {detailedTests.map((test, index) => (
                <div
                  key={index}
                  className={`bg-gradient-to-br ${test.color} p-8 rounded-3xl hover:shadow-xl transition-all cursor-pointer`}
                >
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 flex items-center justify-center bg-white rounded-2xl flex-shrink-0 shadow-md">
                      <i className={`${test.icon} text-3xl text-gray-800`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{test.title}</h3>
                      <p className="text-gray-700 text-sm leading-relaxed">{test.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-16 bg-white p-10 rounded-3xl shadow-lg">
              <div className="flex items-start gap-6">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl flex-shrink-0">
                  <i className="ri-hospital-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">循環器疾患を専門に診ている地域医療機関の先生方へ</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    当院では、心不全精査のため、経胸壁心エコー検査、経食道心エコー検査、運動（薬剤）負荷心エコー検査、各種心筋シンチ検査、MRI検査、心肺運動負荷試験（CPX）を行っております。検査のご要望などございましたら、ご相談いただければ幸いに存じます。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gradient-to-br from-primary-500 to-primary-600 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold mb-6">検査についてのご相談</h2>
            <p className="text-xl text-primary-100 mb-8 leading-relaxed">
              心不全が疑われる症状がある方、検査についてご不明な点がある方は、お気軽にお問い合わせください。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/contact"
                className="px-8 py-4 bg-white text-primary-600 text-base font-semibold rounded-full hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
              >
                お問い合わせ
              </a>
              <a
                href="/regional-cooperation"
                className="px-8 py-4 bg-transparent border-2 border-white text-white text-base font-semibold rounded-full hover:bg-white hover:text-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                地域連携について
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Examination;
